/**
 * Read-only detail-sheet primitives. Used by entity detail sheets across
 * Setup pages (Providers, Locations, Users, Labels, etc.) so each section's
 * grouping is visually obvious — uppercase tracked header on top of a
 * border-divided section.
 *
 * `DetailRow` returns null when the value is empty so empty fields don't
 * leave dangling labels in the UI.
 */

interface DetailSectionProps {
  title: React.ReactNode;
  /** Optional control rendered at the right of the section header (e.g. an Add button). */
  action?: React.ReactNode;
  children: React.ReactNode;
}

export function DetailSection({ title, action, children }: DetailSectionProps): React.ReactElement {
  return (
    <div className="border-t pt-5 pb-2 first:border-t-0 first:pt-2">
      <div className="mb-3 flex min-h-7 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">{title}</h3>
        {action}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

export function DetailRow({ label, value }: DetailRowProps): React.ReactElement | null {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
